import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// ── Section block component ───────────────────────────────────────────────────
function ObjectBlock({ icon, name, description, badge, children, depth = 0, accent = '#bb86fc' }) {
    const [expanded, setExpanded] = useState(true);
    const hasChildren = children && React.Children.count(children) > 0;

    return (
        <div style={{ ...S.block, marginLeft: depth * 28 }}>
            <div style={{ ...S.blockHeader, borderLeftColor: accent }}>
                <div style={S.blockLeft}>
                    <span style={S.blockIcon}>{icon}</span>
                    <div>
                        <div style={S.blockNameRow}>
                            <span style={{ ...S.blockName, color: accent }}>{name}</span>
                            {badge && <span style={{ ...S.badge, backgroundColor: accent + '22', color: accent }}>{badge}</span>}
                        </div>
                        <div style={S.blockDesc}>{description}</div>
                    </div>
                </div>
                {hasChildren && (
                    <button style={S.expandBtn} onClick={() => setExpanded(v => !v)}>
                        {expanded ? '▴' : '▾'}
                    </button>
                )}
            </div>
            {hasChildren && expanded && (
                <div style={S.blockChildren}>
                    {children}
                </div>
            )}
        </div>
    );
}

// ── Field row component ───────────────────────────────────────────────────────
function FieldRow({ label, desc }) {
    return (
        <div style={S.fieldRow}>
            <span style={S.fieldLabel}>{label}</span>
            <span style={S.fieldDesc}>{desc}</span>
        </div>
    );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ label, color }) {
    return (
        <div style={{ ...S.sectionPill, backgroundColor: color + '18', borderColor: color + '44', color }}>
            {label}
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HowItWorks() {
    return (
        <div style={S.page}>

            {/* ── Back to Home ───────────────────────────────────────────── */}
            <div style={S.backBar}>
                <Link to="/" style={S.backLink}>← Back to Home</Link>
            </div>

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <div style={S.hero}>
                <div style={S.heroGlow} />
                <div style={S.heroContent}>
                    <div style={S.eyebrow}>
                        <span style={S.eyebrowPurple}>sharc.fm</span>
                        <span style={S.eyebrowDiv}> | </span>
                        <span style={S.eyebrowSub}>How It Works</span>
                    </div>
                    <h1 style={S.heroTitle}>The Event is the Anchor</h1>
                    <p style={S.heroSubtitle}>
                        Every piece of data in SHARC connects back to a single live performance.
                        Here's how the model fits together — and how you can contribute to it.
                    </p>
                </div>
            </div>

            <div style={S.body}>

                {/* ── The Model ──────────────────────────────────────────── */}
                <section style={S.section}>
                    <h2 style={S.sectionTitle}>The Data Model</h2>
                    <p style={S.sectionIntro}>
                        A <strong style={{ color: '#bb86fc' }}>Show</strong> on SHARC is built around
                        the <strong style={{ color: '#bb86fc' }}>Event</strong> — a single live performance
                        on a specific date at a specific venue. Everything else radiates outward from there.
                        Primary info captures what happened; secondary info captures the context around it.
                    </p>

                    {/* Event anchor card */}
                    <div style={S.eventAnchor}>
                        <div style={S.eventAnchorIcon}>🎤</div>
                        <div>
                            <div style={S.eventAnchorTitle}>Event</div>
                            <div style={S.eventAnchorSub}>The core record — one show, one night, one place</div>
                        </div>
                    </div>

                    {/* Primary Info */}
                    <SectionHeader label="📋 Primary Info — What happened" color="#bb86fc" />

                    <ObjectBlock
                        icon="📅" name="Date" badge="Required"
                        description="The date the event took place. The foundation of every show record."
                        accent="#bb86fc"
                    >
                        <FieldRow label="Event Date" desc="Full date of the performance (day, month, year)" />
                    </ObjectBlock>

                    <ObjectBlock
                        icon="🏟️" name="Venue" badge="Required"
                        description="The physical location where the show took place. Each venue has its own profile with full history."
                        accent="#bb86fc"
                    >
                        <FieldRow label="Name" desc="The venue's official name" />
                        <FieldRow label="City / State / Country" desc="Geographic location, used for maps and breakdowns" />
                        <FieldRow label="Capacity" desc="Maximum number of attendees the venue holds" />
                        <FieldRow label="Links" desc="Website, social media, and other references" />

                        <ObjectBlock
                            icon="🎪" name="Stage" badge="Optional"
                            description="Multi-stage venues (e.g. festivals, amphitheaters) can specify which stage an act performed on."
                            accent="#03dac6"
                            depth={0}
                        >
                            <FieldRow label="Stage Name" desc="e.g. Main Stage, Tent Stage, Balcony" />
                            <FieldRow label="Stage Capacity" desc="Capacity specific to that stage" />
                        </ObjectBlock>
                    </ObjectBlock>

                    <ObjectBlock
                        icon="🎸" name="Artists" badge="Required"
                        description={<>One or more artists on the bill. Each is ranked by lineup position — headliners first, openers last.<br /><span style={{color:'#aaa'}}>Each co-billing creates a relationship link between artists.</span></>}
                        accent="#bb86fc"
                    >
                        <FieldRow label="Lineup Position" desc="1 = headliner, higher numbers = earlier in the show" />
                        <FieldRow label="Billing Type" desc="Headliner; or Support — listed top to bottom (direct support, opener)" />

                        <ObjectBlock
                            icon="👤" name="Members by Artist" badge="Optional"
                            description="Individual members of a band or ensemble who performed at this specific show. Useful for tracking lineups that changed over time."
                            accent="#cf6679"
                            depth={0}
                        >
                            <FieldRow label="Member Name" desc="The individual performer" />
                            <FieldRow label="Role / Instrument" desc="What they played or their role in the group" />
                        </ObjectBlock>
                    </ObjectBlock>

                    {/* Secondary Info */}
                    <SectionHeader label="📎 Secondary Info — The context around it" color="#03dac6" />

                    <ObjectBlock
                        icon="📣" name="Promoter" badge="Optional"
                        description="The individual or company that produced or promoted the event."
                        accent="#03dac6"
                    >
                        <FieldRow label="Name" desc="The promoter's name or company" />
                        <FieldRow label="Location" desc="City / region the promoter operates from" />
                        <FieldRow label="Links" desc="Website, social media references" />

                        <ObjectBlock
                            icon="🎪" name="Festival" badge="Optional"
                            description="If this event was part of a multi-day or multi-stage festival, it can be linked here. A festival groups events under a shared identity."
                            accent="#03dac6"
                            depth={0}
                        >
                            <FieldRow label="Festival Name" desc="e.g. Lollapalooza, Bonnaroo, Pitchfork Fest" />
                            <FieldRow label="Dates" desc="Start and end dates of the full festival run" />
                            <FieldRow label="Promoter" desc="Cascades down from the festival to all child events" />
                        </ObjectBlock>
                    </ObjectBlock>

                    <ObjectBlock
                        icon="🔗" name="Assets" badge="Optional"
                        description="Supporting media and references that enrich the event record."
                        accent="#03dac6"
                    >
                        <FieldRow label="Links" desc="Setlist references, press coverage, official recordings, social posts" />
                        <FieldRow label="Images" desc="Flyers, photos, and promotional artwork tied to the event" />
                        <FieldRow label="Footnotes" desc="Editor notes or contextual detail about the show" />
                    </ObjectBlock>

                    <ObjectBlock
                        icon="📊" name="Estimated Attendance" badge="Optional"
                        description="An approximate headcount for the event. Used in venue capacity analytics and artist reach tracking."
                        accent="#03dac6"
                    >
                        <FieldRow label="Attendance Figure" desc="Estimated number of attendees at the show" />
                        <FieldRow label="Source" desc="Where the estimate came from (press, venue, artist, etc.)" />
                    </ObjectBlock>
                </section>

                {/* ── How to Contribute ──────────────────────────────────── */}
                <section style={{ ...S.section, marginTop: '64px' }}>
                    <h2 style={S.sectionTitle}>How to Contribute</h2>
                    <p style={S.sectionIntro}>
                        SHARC grows through curated contributions. Every show added makes the archive
                        more valuable for artists, fans, and venues alike. Here's how to get started.
                    </p>

                    <div style={S.stepsGrid}>
                        {[
                            {
                                step: '01',
                                icon: '🎸',
                                title: 'Add an Artist',
                                desc: 'Start by creating an artist profile if it doesn\'t exist yet. Include their location, genre tags, and any biographical info.',
                                link: '/add-artist',
                                linkLabel: 'Add Artist →',
                            },
                            {
                                step: '02',
                                icon: '🏟️',
                                title: 'Add a Venue',
                                desc: 'Create the venue where the show took place. Capacity, location, and stage info can all be added here.',
                                link: '/add-venue',
                                linkLabel: 'Add Venue →',
                            },
                            {
                                step: '03',
                                icon: '🎤',
                                title: 'Add the Event',
                                desc: 'Create the event with a date, venue, and artist lineup. This is the core record that everything else hangs off.',
                                link: '/add-event',
                                linkLabel: 'Add Event →',
                            },
                            {
                                step: '04',
                                icon: '✨',
                                title: 'Enrich It',
                                desc: 'Add members, attendance estimates, footnotes, images, and links to bring the record to life.',
                                link: null,
                                linkLabel: null,
                            },
                        ].map((s, i) => (
                            <div key={i} style={S.stepCard}>
                                <div style={S.stepNumber}>{s.step}</div>
                                <div style={S.stepIcon}>{s.icon}</div>
                                <div style={S.stepTitle}>{s.title}</div>
                                <div style={S.stepDesc}>{s.desc}</div>
                                {s.link && (
                                    <Link to={s.link} style={S.stepLink}>{s.linkLabel}</Link>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── CTA ────────────────────────────────────────────────── */}
                <section style={S.cta}>
                    <h2 style={S.ctaTitle}>Ready to explore?</h2>
                    <p style={S.ctaSubtitle}>Browse the archive or start adding shows.</p>
                    <div style={S.ctaButtons}>
                        <Link to="/artists" style={S.ctaPrimary}>Browse Artists</Link>
                        <Link to="/venues" style={S.ctaGhost}>Browse Venues</Link>
                        <Link to="/add-event" style={S.ctaGhost}>Add a Show</Link>
                    </div>
                </section>

            </div>
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: {
        backgroundColor: '#121212',
        color: '#e0e0e0',
        minHeight: '100vh',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
    },

    // Hero
    hero: {
        position: 'relative',
        overflow: 'hidden',
        padding: '72px 40px 56px',
        textAlign: 'center',
        borderBottom: '1px solid #2a2a2a',
    },
    heroGlow: {
        position: 'absolute',
        top: '-60px', left: '50%',
        transform: 'translateX(-50%)',
        width: '600px', height: '300px',
        background: 'radial-gradient(ellipse at center, rgba(187,134,252,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    heroContent: { position: 'relative', zIndex: 2, maxWidth: '640px', margin: '0 auto' },
    eyebrow: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '8px', marginBottom: '20px', fontSize: '13px',
        letterSpacing: '0.08em', textTransform: 'uppercase',
    },
    eyebrowPurple: { color: '#bb86fc', fontWeight: 700 },
    eyebrowDiv: { color: '#555' },
    eyebrowSub: { color: '#888' },
    heroTitle: {
        fontSize: 'clamp(32px, 6vw, 52px)', fontWeight: 800,
        color: '#f0f0f0', margin: '0 0 16px', letterSpacing: '-0.02em',
    },
    heroSubtitle: {
        fontSize: '16px', lineHeight: 1.7, color: '#aaa', margin: 0,
    },

    // Back bar
    backBar: {
        padding: '12px 24px',
        borderBottom: '1px solid #1e1e1e',
    },
    backLink: {
        fontSize: '13px',
        color: '#888',
        textDecoration: 'none',
        fontWeight: 500,
        letterSpacing: '0.02em',
    },

    // Body
    body: { maxWidth: '820px', margin: '0 auto', padding: '56px 24px 80px' },

    // Section
    section: {},
    sectionTitle: {
        fontSize: '26px', fontWeight: 700, color: '#f0f0f0',
        margin: '0 0 12px', letterSpacing: '-0.01em',
    },
    sectionIntro: {
        fontSize: '15px', lineHeight: 1.75, color: '#aaa',
        margin: '0 0 36px',
    },

    // Event anchor
    eventAnchor: {
        display: 'flex', alignItems: 'center', gap: '16px',
        backgroundColor: '#1e1e1e',
        border: '2px solid #bb86fc',
        borderRadius: '10px',
        padding: '20px 24px',
        marginBottom: '8px',
    },
    eventAnchorIcon: { fontSize: '32px' },
    eventAnchorTitle: { fontSize: '20px', fontWeight: 800, color: '#bb86fc' },
    eventAnchorSub: { fontSize: '13px', color: '#888', marginTop: '2px' },

    // Section pill
    sectionPill: {
        display: 'inline-block',
        fontSize: '12px', fontWeight: 600,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        padding: '5px 14px', borderRadius: '20px',
        border: '1px solid',
        margin: '24px 0 12px',
    },

    // Object block
    block: {
        marginBottom: '6px',
    },
    blockHeader: {
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: '12px',
        backgroundColor: '#1a1a1a',
        border: '1px solid #2e2e2e',
        borderLeft: '3px solid #bb86fc',
        borderRadius: '8px',
        padding: '14px 16px',
        cursor: 'default',
    },
    blockLeft: { display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 },
    blockIcon: { fontSize: '20px', flexShrink: 0, marginTop: '1px' },
    blockNameRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' },
    blockName: { fontSize: '15px', fontWeight: 700 },
    badge: {
        fontSize: '10px', fontWeight: 600, letterSpacing: '0.05em',
        textTransform: 'uppercase', padding: '2px 7px', borderRadius: '10px',
    },
    blockDesc: { fontSize: '13px', color: '#999', lineHeight: 1.55 },
    expandBtn: {
        background: 'none', border: 'none', color: '#666',
        fontSize: '12px', cursor: 'pointer', padding: '0 4px',
        flexShrink: 0, marginTop: '2px',
    },
    blockChildren: {
        marginLeft: '28px',
        marginTop: '4px',
        paddingLeft: '16px',
        borderLeft: '1px dashed #333',
    },

    // Field rows
    fieldRow: {
        display: 'flex', alignItems: 'baseline', gap: '10px',
        padding: '7px 12px',
        borderBottom: '1px solid #222',
        backgroundColor: '#161616',
        borderRadius: '4px',
        marginBottom: '2px',
    },
    fieldLabel: {
        fontSize: '13px', fontWeight: 600, color: '#ccc',
        minWidth: '160px', flexShrink: 0,
    },
    fieldDesc: { fontSize: '13px', color: '#777', lineHeight: 1.5 },

    // Steps grid
    stepsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
    },
    stepCard: {
        backgroundColor: '#1a1a1a',
        border: '1px solid #2e2e2e',
        borderRadius: '10px',
        padding: '24px 20px',
    },
    stepNumber: {
        fontSize: '11px', fontWeight: 700, color: '#bb86fc',
        letterSpacing: '0.1em', marginBottom: '10px',
    },
    stepIcon: { fontSize: '26px', marginBottom: '10px' },
    stepTitle: { fontSize: '15px', fontWeight: 700, color: '#e0e0e0', marginBottom: '8px' },
    stepDesc: { fontSize: '13px', color: '#888', lineHeight: 1.6, marginBottom: '14px' },
    stepLink: {
        display: 'inline-block', fontSize: '13px', fontWeight: 600,
        color: '#bb86fc', textDecoration: 'none',
    },

    // CTA
    cta: {
        marginTop: '64px',
        textAlign: 'center',
        padding: '48px 24px',
        backgroundColor: '#1a1a1a',
        border: '1px solid #2e2e2e',
        borderRadius: '12px',
    },
    ctaTitle: { fontSize: '24px', fontWeight: 700, color: '#f0f0f0', margin: '0 0 8px' },
    ctaSubtitle: { fontSize: '15px', color: '#888', margin: '0 0 28px' },
    ctaButtons: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
    ctaPrimary: {
        display: 'inline-block', padding: '11px 24px',
        backgroundColor: '#bb86fc', color: '#121212',
        fontWeight: 700, fontSize: '14px', borderRadius: '6px',
        textDecoration: 'none',
    },
    ctaGhost: {
        display: 'inline-block', padding: '11px 24px',
        backgroundColor: 'transparent', color: '#bb86fc',
        fontWeight: 600, fontSize: '14px', borderRadius: '6px',
        textDecoration: 'none', border: '1px solid #bb86fc',
    },
};
